import type { Column } from 'components/DataGrid';
import type { useStore } from 'jotai';
import type { GroupPatch, RowPatch } from '../diff';
import type { GridRow, GridSummaryRow, RowOrGroup } from '../types';

/** Source grid domain for clipboard payloads — deliberately not per-block, so
 * plain values paste across plans; identity-carrying fields defend themselves
 * in their custom `paste`. */
export const PLAN_GRID_ID = 'plan';

/** Structured content of one copied cell. `field` is the write target (a
 * PlanRow field), not the column key — the same column resolves to different
 * fields per row kind, and the same field can live in different columns
 * across strategies. */
export type PlanCellPayload = {
  readonly field: string;
  readonly value: unknown;
};

/** The copied rectangle, rowKeys × columnKeys. `null` marks cells that don't
 * participate (groups, unconfigured branches) — skipped on paste, but they
 * keep their offset so the geometry survives. */
export type PlanClipboardData = readonly (readonly (PlanCellPayload | null)[])[];

/** Handed to custom `paste` by the hook. Column definitions are module
 * statics and can't call hooks, so plan-state lookups (option lists, current
 * rows) go through the jotai store. */
export type PlanClipboardContext = {
  readonly store: ReturnType<typeof useStore>;
};

/** Per-branch clipboard overrides. An editable branch participates by
 * default (all behavior derived from `field`); supply this object to override
 * individual members (scalar/copy/compatible/paste). Opt out entirely with
 * `clipboard: false` on the branch (see FieldBranch/GroupBranch). */
export type PlanClipboardCellConfig<R extends RowOrGroup = GridRow, P = RowPatch> = {
  /** text/plain (TSV) projection. Default: `row[field]` for primitives, `''` otherwise. */
  readonly scalar?: (row: R) => string;
  /** Payload value. Default: `row[field]`. */
  readonly copy?: (row: R) => unknown;
  /**
   * Predicate deciding whether an incoming payload may paste into this cell,
   * remapping its value onto this cell's field. Default: exact field match.
   * Use to cross-paste structurally-identical fields (e.g. client_price ↔
   * provider_price). Ignored when a custom `paste` is supplied — that handler
   * decides compatibility itself.
   */
  readonly compatible?: (payload: PlanCellPayload) => boolean;
  /**
   * Default: field match (own `field` or a `compatible` one) → `{ id, [field]:
   * value }`. A custom handler receives payloads of any field (it decides
   * compatibility), revalidates identity-carrying values, may toast on semantic
   * rejection, and bails with null. Never invoked on locked cells.
   */
  readonly paste?: (
    payload: PlanCellPayload,
    args: { row: R; ctx: PlanClipboardContext },
  ) => P | null | Promise<P | null>;
};

/** Clipboard behavior of one cell, resolved against its row. `P` is the
 * patch shape its `paste` yields — `RowPatch` for plan rows, `GroupPatch` for
 * budget groups. */
export type ResolvedClipboardCell<P = RowPatch> = {
  readonly field: string;
  readonly scalar: string;
  readonly copy: () => PlanCellPayload;
  readonly paste: (payload: PlanCellPayload, ctx: PlanClipboardContext) => P | null | Promise<P | null>;
};

/** Plan-side column extension. `useCalculatedColumns` spreads the raw column,
 * so the resolver survives into the `column` handed to the grid's clipboard
 * callbacks — cast at that boundary. */
export type PlanColumn = Column<RowOrGroup, GridSummaryRow> & {
  readonly clipboardCell?: (row: RowOrGroup) => ResolvedClipboardCell<RowPatch | GroupPatch> | null;
};

/** Payloads cross the OS clipboard as JSON — revalidate shape, not just trust the generic. */
export function isPlanCellPayload(data: unknown): data is PlanCellPayload {
  return typeof data === 'object' && data !== null && typeof (data as { field?: unknown }).field === 'string';
}

export function defaultScalar(value: unknown): string {
  switch (typeof value) {
    case 'string':
      return value;
    case 'number':
    case 'boolean':
      return String(value);
    default:
      return '';
  }
}
